import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Download, GitFork, Palette, Sparkles, Wand2, type LucideIcon } from 'lucide-react';
import { Button, Modal, Spinner } from '@/components/ui';
import { toast } from '@/components/feedback/toast';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/config/constants';
import { queryKeys } from '@/lib/queryClient';
import { useAuth } from '@/app/providers';
import { useAuthModal } from '@/features/auth/authModalStore';
import { THEMES, type Theme } from '@/features/theme/themes';
import { themeAssetExists } from '@/features/theme/themeAssets';
import { useThemeSelection } from '@/features/theme/useThemeSelection';
import { ThemeThumbnail } from '@/features/theme/components/ThemeThumbnail';
import { ThemeDocThumbnail } from '@/components/theme/ThemeDocThumbnail';
import { useMyCommunities } from '@/features/communities/hooks/useMyCommunities';
import { applyThemeToCommunity, forkTheme } from '@/features/studio/api/themes';
import { usePublicThemes, themeKeys } from '@/features/studio/hooks/useThemeDoc';
import type { ThemeRow } from '@/types';

/**
 * Marketplace — the storefront for themes. Two shelves: WINR's official skins
 * (the built-in catalog, applied as your personal background) and community
 * themes — real published `themes` rows anyone can remix, and community owners
 * can equip live. No more empty-state placeholder: this is the economy layer's
 * front door.
 */
export function MarketplacePage() {
  const navigate = useNavigate();
  const { activeId, pick } = useThemeSelection();
  const openStudio = () => navigate(ROUTES.themeStudio);

  return (
    <div className="mx-auto w-full max-w-[1800px] px-5 sm:px-8">
      <header className="mb-9 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-[30px] font-black tracking-[-0.8px] text-white sm:text-[34px]">
            Marketplace
          </h1>
          <p className="mt-1 text-[14px] font-semibold text-lo">
            Create or use themes to customize your community.
          </p>
        </div>
        <Button className="flex-none gap-1.5" onClick={openStudio}>
          <Wand2 size={16} strokeWidth={2.2} />
          Create a theme
        </Button>
      </header>

      <section className="mb-12">
        <SectionHeading icon={Sparkles} title="WINR Themes" caption="Official backgrounds — free to use" />
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,272px),1fr))] gap-4">
          {THEMES.map((theme) => (
            <SkinCard
              key={theme.id}
              theme={theme}
              active={theme.id === activeId}
              unavailable={theme.file != null && !themeAssetExists(theme.file)}
              onUse={() => pick(theme.id)}
            />
          ))}
        </div>
      </section>

      <section>
        <SectionHeading icon={Palette} title="Community themes" caption="Made by creators — remix or equip" />
        <CommunityThemesShelf onCreate={openStudio} />
      </section>
    </div>
  );
}

function CommunityThemesShelf({ onCreate }: { onCreate: () => void }) {
  const { data: themes, isLoading } = usePublicThemes();

  if (isLoading) {
    return (
      <div className="grid place-items-center rounded-2xl border border-dashed border-card-line py-14">
        <Spinner />
      </div>
    );
  }
  if (!themes || themes.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-card-line bg-white/[0.02] px-6 py-14 text-center">
        <span className="grid h-14 w-14 place-items-center rounded-full border border-white/[0.08] bg-white/[0.04] text-mid">
          <Palette size={24} strokeWidth={1.8} />
        </span>
        <h3 className="text-lg font-extrabold tracking-[-0.3px] text-hi">No community themes yet</h3>
        <p className="max-w-[440px] text-[13.5px] leading-relaxed text-mid">
          Be the first — design a theme in WINR Studio and publish it publicly to see it here.
        </p>
        <Button className="mt-1 gap-2" onClick={onCreate}>
          <Wand2 size={15} strokeWidth={2.2} />
          Open WINR Studio
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,272px),1fr))] gap-4">
      {themes.map((t) => (
        <CommunityThemeCard key={t.id} theme={t} />
      ))}
    </div>
  );
}

function CommunityThemeCard({ theme }: { theme: ThemeRow }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const openAuth = useAuthModal((s) => s.openAuth);
  const [equipOpen, setEquipOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function remix(): Promise<void> {
    if (!user) return openAuth();
    setBusy(true);
    try {
      const draft = await forkTheme(theme.id);
      navigate(`${ROUTES.themeStudio}?t=${draft.id}`);
    } catch (e) {
      toast((e as Error).message || 'Could not remix theme', 'error');
      setBusy(false);
    }
  }

  return (
    <div className="group overflow-hidden rounded-xl border border-card-line bg-white/[0.02] transition-all duration-200 ease-cinematic hover:-translate-y-0.5 hover:border-white/25">
      <div className="relative aspect-[16/10] overflow-hidden">
        <ThemeDocThumbnail
          doc={theme.doc}
          className="absolute inset-0 transition-transform duration-300 group-hover:scale-105"
        />
        {theme.install_count > 0 && (
          <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-pill bg-black/55 px-2 py-0.5 text-[10px] font-bold text-white/85">
            <Download size={10} strokeWidth={2.6} /> {theme.install_count}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 p-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-bold text-hi">{theme.name}</div>
          <div className="text-[11px] font-semibold text-lo">Free</div>
        </div>
        <div className="flex flex-none gap-1.5">
          <Button size="sm" variant="outline" className="gap-1" disabled={busy} onClick={() => void remix()}>
            <GitFork size={12} strokeWidth={2.4} /> Remix
          </Button>
          <Button
            size="sm"
            className="flex-none"
            disabled={busy}
            onClick={() => (user ? setEquipOpen(true) : openAuth())}
          >
            Use
          </Button>
        </div>
      </div>

      <EquipDialog open={equipOpen} onClose={() => setEquipOpen(false)} theme={theme} />
    </div>
  );
}

/** Pick one of your owned communities to wear this theme. */
function EquipDialog({ open, onClose, theme }: { open: boolean; onClose: () => void; theme: ThemeRow }) {
  const qc = useQueryClient();
  const { data: mine = [] } = useMyCommunities();
  const owned = mine.filter((m) => m.relationship === 'owner');
  const [busyId, setBusyId] = useState<string | null>(null);

  async function equip(communityId: string): Promise<void> {
    setBusyId(communityId);
    try {
      await applyThemeToCommunity(theme.id, communityId);
      void qc.invalidateQueries({ queryKey: queryKeys.communities.all });
      void qc.invalidateQueries({ queryKey: themeKeys.public });
      toast('Theme applied — your members see it now', 'ok');
      onClose();
    } catch (e) {
      toast((e as Error).message || 'Could not apply theme', 'error');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <div className="mb-1 text-lg font-extrabold text-hi">Use “{theme.name}”</div>
      <p className="mb-4 text-[12.5px] text-mid">Choose a community you own to apply it to.</p>
      {owned.length === 0 ? (
        <p className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-3 text-[12.5px] text-mid">
          You don't own a community yet — create one first, then equip themes from here.
        </p>
      ) : (
        <div className="space-y-1.5">
          {owned.map((m) => (
            <button
              key={m.community.id}
              type="button"
              disabled={busyId != null}
              onClick={() => void equip(m.community.id)}
              className="flex w-full items-center gap-2.5 rounded-lg border border-white/10 px-3 py-2.5 text-left transition-colors hover:bg-white/[0.05] disabled:opacity-50"
            >
              <span
                className="grid h-7 w-7 flex-none place-items-center rounded-md text-[12px] font-black text-black"
                style={{ background: m.community.color ?? '#54b9fe' }}
              >
                {(m.community.name ?? 'C').charAt(0).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13.5px] font-bold text-hi">
                {m.community.name}
              </span>
              {busyId === m.community.id && <Spinner className="h-4 w-4" />}
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

function SectionHeading({
  icon: Icon,
  title,
  caption,
}: {
  icon: LucideIcon;
  title: string;
  caption: string;
}) {
  return (
    <div className="mb-4 flex items-center gap-2.5">
      <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-accent-soft text-accent">
        <Icon size={15} strokeWidth={2.2} />
      </span>
      <div>
        <h2 className="text-base font-extrabold leading-tight text-hi">{title}</h2>
        <p className="text-[11px] font-semibold text-lo">{caption}</p>
      </div>
    </div>
  );
}

function SkinCard({
  theme,
  active,
  unavailable,
  onUse,
}: {
  theme: Theme;
  active: boolean;
  unavailable: boolean;
  onUse: () => void;
}) {
  return (
    <div
      className={cn(
        'group overflow-hidden rounded-xl border bg-white/[0.02] transition-all duration-200 ease-cinematic',
        active
          ? 'border-accent shadow-[0_0_0_2px_var(--winr-accent-soft)]'
          : 'border-card-line hover:-translate-y-0.5 hover:border-white/25',
        unavailable && 'opacity-50',
      )}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <ThemeThumbnail
          theme={theme}
          className="absolute inset-0 transition-transform duration-300 group-hover:scale-105"
        />
        <span className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
        {active && (
          <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-pill bg-accent px-2 py-0.5 text-[10px] font-bold text-black shadow-[0_2px_8px_rgba(0,0,0,0.45)]">
            <Check size={11} strokeWidth={3} /> Equipped
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 p-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-bold text-hi">{theme.name}</div>
          <div className="text-[11px] font-semibold text-lo">{unavailable ? 'Coming soon' : 'Free'}</div>
        </div>
        <Button
          size="sm"
          variant={active ? 'outline' : 'primary'}
          disabled={unavailable || active}
          onClick={onUse}
          className="flex-none"
        >
          {active ? 'In use' : 'Use'}
        </Button>
      </div>
    </div>
  );
}
